import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IoClose, IoRocketOutline, IoTrashOutline } from 'react-icons/io5';
import { collection, getDocs, addDoc, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const AdminUpdatesPage = () => {
  const [updates, setUpdates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ version: '', content: '' });

  const fetchUpdates = async () => {
    try {
      const q = query(collection(db, 'app_updates'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setUpdates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('업데이트 목록 로드 실패:', err);
    }
  };

  useEffect(() => { fetchUpdates(); }, []);

  const handleSubmit = async () => {
    if (!form.version.trim()) return;
    try {
      await addDoc(collection(db, 'app_updates'), {
        version: form.version.trim(),
        content: form.content.trim(),
        date: new Date().toISOString().split('T')[0],
        createdAt: serverTimestamp(),
      });
      setForm({ version: '', content: '' });
      setShowForm(false);
      fetchUpdates();
    } catch (err) {
      console.error('업데이트 등록 실패:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 버전을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'app_updates', id));
      setUpdates(prev => prev.filter(u => u.id !== id));
    } catch (err) {
      console.error('삭제 실패:', err);
    }
  };

  const fmtDate = (ts) => {
    if (!ts) return '-';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <Container>
      <PageHeader>
        <PageTitle>웹 업데이트</PageTitle>
        <AddBtn onClick={() => setShowForm(true)}>
          <IoRocketOutline size={16} />
          새 버전 등록
        </AddBtn>
      </PageHeader>

      <TableWrap>
        <Table>
          <thead>
            <Tr>
              <Th>버전</Th>
              <Th>업데이트 내용</Th>
              <Th>등록일</Th>
              <Th style={{ width: 60 }}>삭제</Th>
            </Tr>
          </thead>
          <tbody>
            {updates.map((u, i) => (
              <Tr key={u.id}>
                <Td style={{ whiteSpace: 'nowrap' }}>
                  <VersionBadge $latest={i === 0}>{u.version}</VersionBadge>
                  {i === 0 && <LatestTag>최신</LatestTag>}
                </Td>
                <Td>{u.content || '-'}</Td>
                <Td $sub>{fmtDate(u.createdAt)}</Td>
                <Td>
                  <DeleteBtn onClick={() => handleDelete(u.id)}>
                    <IoTrashOutline size={16} />
                  </DeleteBtn>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        {updates.length === 0 && <Empty>등록된 버전이 없습니다</Empty>}
      </TableWrap>

      {showForm && (
        <Overlay onClick={() => setShowForm(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>새 버전 등록</ModalTitle>
              <CloseBtn onClick={() => setShowForm(false)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <Label>버전 (예: 1.0.1)</Label>
              <Input
                placeholder="1.0.0"
                value={form.version}
                onChange={e => setForm(prev => ({ ...prev, version: e.target.value }))}
                autoFocus
              />
              <Label>업데이트 내용</Label>
              <Textarea
                placeholder="업데이트 내용을 입력하세요"
                value={form.content}
                onChange={e => setForm(prev => ({ ...prev, content: e.target.value }))}
                rows={4}
              />
              <BtnRow>
                <Btn $cancel onClick={() => setShowForm(false)}>취소</Btn>
                <Btn onClick={handleSubmit} disabled={!form.version.trim()}>등록</Btn>
              </BtnRow>
            </ModalBody>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
};

export default AdminUpdatesPage;

const Container = styled.div``;
const PageHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;`;
const PageTitle = styled.h1`font-size: 22px; font-weight: 700; color: #111827;`;
const AddBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 9px 18px; border-radius: 8px;
  background: #4A7CFE; color: white; font-size: 13px; font-weight: 600;
  &:hover { background: #3B6DE8; }
`;

const TableWrap = styled.div`background: #fff; border-radius: 12px; border: 1px solid #EAEDF3; overflow: hidden;`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Tr = styled.tr`
  &:not(:last-child) td { border-bottom: 1px solid #F3F4F6; }
  &:hover td { background: #F9FAFB; }
`;
const Th = styled.th`text-align: left; font-size: 13px; color: #9CA3AF; font-weight: 500; padding: 12px 16px; border-bottom: 1px solid #EAEDF3; background: #FAFBFC;`;
const Td = styled.td`font-size: ${p => p.$sub ? '13px' : '14px'}; color: ${p => p.$sub ? '#9CA3AF' : '#111827'}; padding: 14px 16px;`;
const Empty = styled.div`text-align: center; padding: 48px; color: #9CA3AF; font-size: 14px;`;

const VersionBadge = styled.span`
  font-size: 15px; font-weight: 600; color: ${p => p.$latest ? '#4A7CFE' : '#111827'};
`;
const LatestTag = styled.span`
  font-size: 11px; padding: 2px 8px; border-radius: 6px; margin-left: 8px;
  background: #EEF2FF; color: #4A7CFE; font-weight: 600;
`;
const DeleteBtn = styled.button`
  color: #EF4444; display: flex; padding: 6px; border-radius: 8px;
  &:hover { background: #FEF2F2; }
`;

const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 200;`;
const Modal = styled.div`background: #fff; border-radius: 14px; width: 440px; max-width: calc(100vw - 32px);`;
const ModalHeader = styled.div`display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #F3F4F6;`;
const ModalTitle = styled.h3`font-size: 18px; font-weight: 600; color: #111827;`;
const CloseBtn = styled.button`color: #9CA3AF; display: flex;`;
const ModalBody = styled.div`padding: 24px;`;
const Label = styled.div`font-size: 13px; color: #6B7280; margin-bottom: 6px; margin-top: 12px; &:first-child { margin-top: 0; }`;
const Input = styled.input`
  width: 100%; padding: 10px 12px; font-size: 14px; border: 1px solid #E5E7EB;
  border-radius: 8px; outline: none; box-sizing: border-box;
  &:focus { border-color: #4A7CFE; box-shadow: 0 0 0 3px #EEF2FF; }
`;
const Textarea = styled.textarea`
  width: 100%; padding: 10px 12px; font-size: 14px; border: 1px solid #E5E7EB;
  border-radius: 8px; outline: none; box-sizing: border-box; resize: vertical; font-family: inherit;
  &:focus { border-color: #4A7CFE; box-shadow: 0 0 0 3px #EEF2FF; }
`;
const BtnRow = styled.div`display: flex; gap: 10px; margin-top: 20px;`;
const Btn = styled.button`
  flex: 1; padding: 11px; border-radius: 8px; font-size: 14px; font-weight: 600; border: none; cursor: pointer;
  background: ${p => p.$cancel ? '#F3F4F6' : '#4A7CFE'};
  color: ${p => p.$cancel ? '#6B7280' : '#fff'};
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.4; cursor: default; }
`;
